import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import moment from "moment";
import toast from "react-hot-toast";
import {
  Search,
  Plus,
  Download,
  Upload,
  RefreshCw,
  LayoutGrid,
  List as ListIcon,
  Phone,
  Home,
  EyeOff,
  StickyNote,
  MoreHorizontal,
  Pencil,
  ChevronUp,
  ChevronDown,
  CheckCircle2,
  Database as DatabaseIcon,
  XCircle,
  X,
  Mail,
  ExternalLink,
  UserPlus,
  Hourglass,
  Undo2,
  Trash2,
  Tag,
  ArrowRight,
  Users,
} from "lucide-react";
import { hideLostFromProspects, markSellerLeadListed, moveLeadOnBoard, setSellerLeadAction, updateLead } from "../../../api/lead";
import {
  patchProperty,
  markPropertyListed,
  markPropertyLost,
  withdrawProperty,
  moveLostPropertyToAppraisal,
  movePropertyBack,
  deleteProperty,
  getAgents,
} from "../../../api/agent";
import PublicPropertyLink from "../../properties/components/PublicPropertyLink";
import PropertyProfileLink from "../../properties/components/PropertyProfileLink";
import UpdatePropertyInfoButton from "../../leads/components/UpdatePropertyInfoButton";
import { invalidateCache } from "../../../lib/cache";
import { formatMelbourneShortDate } from "../../../utils/melbourneTime";
import AgencyScopeSwitch from "../../../components/common/AgencyScopeSwitch";
import AgentScopePicker, { useAgentScope } from "../components/AgentScopePicker";
import AssignPropertyAgentModal from "../components/AssignPropertyAgentModal";
import AddSellerLeadModal from "../../leads/components/AddSellerLeadModal";
import AssignLeadModal from "../../leads/components/AssignLeadModal";
import AddToDatabaseModal from "../../leads/components/AddToDatabaseModal";
import LeadNotesModal from "../../leads/components/LeadNotesModal";
import PropertyNotesModal from "../../../components/notes/PropertyNotesModal";
import LeadAppraisalEditModal from "../../leads/components/LeadAppraisalEditModal";
import IgnoreLeadReasonModal from "../../leads/components/IgnoreLeadReasonModal";
import PipelineAppraisalEditModal from "../../leads/components/PipelineAppraisalEditModal";
import ContactLink from "../../contacts/components/ContactLink";
import LeadSourceBadge from "../../dashboard/components/LeadSourceBadge";
import RentalLeadPill from "../../leads/components/RentalLeadPill";
import ProspectBuyersModal from "../components/ProspectBuyersModal";
import { fetchBuyerCounts, matchKey, matchable } from "../buyerMatch";
import { LOST_REASONS, LISTING_LOST_REASONS } from "../../leads/constants/lostReasons";
import LostReasonPicker from "../../leads/components/LostReasonPicker";
import { parseAuAddress } from "../../leads/utils/auAddress";
import DatabasePage from "../../database/pages/DatabasePage";
import ImportWizard from "../../database/components/ImportWizard";
import { getProspectLists } from "../../../api/database";
import { canIncludeColleagues } from "../../../utils/roleAccess";
import MarkSoldModal from "../../../components/common/MarkSoldModal";
import {
  PROSPECT_STAGES,
  STAGE_BY_ID,
  EMPTY_COUNTS,
  isPipelineStage,
  MARKETS,
  SOLD_WINDOW_DAYS,
  loadProspectsPipeline,
  formatMoney,
  daysSince,
  isAtRisk,
  matchesMarket,
  matchesSearch,
  TEMPERATURES,
  TEMPERATURE_BY_ID,
} from "../prospectsPipeline";

const TEAL = "#55C5D0";

const EMPTY_BOARD = {
  items: [],
  counts: EMPTY_COUNTS,
  atRisk: 0,
  total: 0,
};

const MARKET_LABEL = { off_market: "Off market", on_market: "On market" };

// A dollar figure in full, the way the live CRM's appraisal cards print it.
const fullMoney = (n) =>
  // A dollar amount, not a date, so the Melbourne date rule does not apply.
  // eslint-disable-next-line no-restricted-syntax
  `$${Math.round(Number(n) || 0).toLocaleString()}`;

// A listing that is lost is withdrawn, with the listing reasons; anything
// earlier (a lead or an appraisal) is marked Lost with the lead reasons.
const isListing = (item) => item?.kind === "property" && (item.stage === "listed" || item.stage === "for_sale");

// A sale is final on the board: a Sold card stays in Sold.
const isSold = (item) => item?.stage === "sold";

// On realestate.com.au, whether the CRM published it there or it came in from Vault.
const isOnRea = (item) => Boolean(item?.raw?.published_on_rea || item?.raw?.rea_listing_id || item?.raw?.rea_unique_id);

/**
 * Every card can go to every column (Christian Gravias, Monday 13331968296,
 * 21 Oct 2026: "Please fix this where any card can be moved anywhere. I dont
 * want restrictions"), except a Sold one, which stays put (22 Oct: "Dont allow
 * sold to be moved. Everything else is fine to be moved"). Any card can still
 * go TO Sold. Each move still does what that column stands for
 * (Monday 12988442847, 12 Oct: "Yes this is correct"):
 *
 *   -> New        back to the seller lead, not called yet
 *   -> Connected  the lead marked Called (from a property: back to its lead)
 *   -> Long Term  the lead parked as not selling soon
 *   -> Appraisal  a lead opens Add Appraisal filled in from it; a property is
 *                 an appraisal again, off the website and portals
 *   -> Listed     the one-click Listed draft
 *   -> For Sale   a live listing on the CRM (the portals are still uploaded
 *                 from the listing itself)
 *   -> Sold       the Mark as Sold form, same as on the property page
 *   -> Lost       asks why, then the same Lost as the card menu (a listing
 *                 is withdrawn with the listing reasons instead)
 *
 * A listing on REA that moves anywhere but For Sale comes off REA
 * (CRM-2026-00115).
 */
const allowedMoves = (item) =>
  item && !isSold(item) ? PROSPECT_STAGES.map((s) => s.id).filter((id) => id !== item.stage) : [];

// Appraisals and Connected / Long Term leads carry Cold / Warm / Hot.
const hasTemperature = (item) =>
  item?.stage === "appraisal" || (item?.kind === "lead" && (item?.stage === "connected" || item?.stage === "long_term"));

/**
 * Prospects: Database, Seller Leads, Off Market Pipeline and On Market Pipeline
 * as ONE page with two modes (Christian Gravias, Monday 12988442847, 29 Sep
 * 2026: "We need to update the side bars & the page. We are bringing database
 * into this 1 page and also combining on market & off market leads. You have
 * missed the kan ban & the list.").
 *
 * Pipeline is where an agent works day to day, so it is what the page opens
 * on: every seller lead staged New, Connected, Appraisal, Listed, Sold, as a
 * kanban or as a sortable list, with one market chip instead of two boards.
 * Database is the existing Database page, unchanged, one click away; its bulk
 * bar's "Add as Appraisal" and "Move to Long Term" push rows into the pipeline.
 *
 * The four old pages keep their routes. Their extra views (Actioned leads, the
 * Lost appraisals column, the per-agent board switcher) are linked from the
 * foot of the board, so nothing they did has been taken away.
 */
const ProspectsPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const mode = searchParams.get("mode") === "database" ? "database" : "pipeline";
  const view = searchParams.get("view") === "list" ? "list" : "kanban";
  const market = MARKETS.some((m) => m.id === searchParams.get("market")) ? searchParams.get("market") : "all";
  const stageFilter = STAGE_BY_ID[searchParams.get("stage")] ? searchParams.get("stage") : "all";

  // One URL param at a time, keeping the rest, so a link from the dashboard
  // (e.g. ?view=list&stage=connected) survives the agent changing the market.
  const setParam = useCallback(
    (key, value, fallback) => {
      const next = new URLSearchParams(searchParams);
      if (value === undefined || value === null || value === fallback) next.delete(key);
      else next.set(key, value);
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  // Same switch, and the same saved choice, as the appraisal boards: the
  // principal can flip to the whole office, everyone else sees their own.
  // The agent picker narrows it to one agent or a team (see AgentScopePicker).
  // Neither the agent nor the team is remembered, so the board you land on is
  // always your own, unless a Dashboard link asked for one agent (?agent=).
  const scope = useAgentScope({ initialAgentId: searchParams.get("agent") });
  const { agencyWide, setAgencyWide, canSwitch, pickerMode, boardAgentId, team, ownOnly } = scope;

  const [board, setBoard] = useState(EMPTY_BOARD);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState({ key: "stage", dir: "asc" });
  // The Hot / Cold order on the kanban's Appraisal and Connected columns, the
  // same toggle as the list's Temperature heading (Christian Gravias, Monday
  // 12988442847, 14 Oct 2026: "Add the temperature toggle in the Kanban view of
  // appraisal also"; 16 Oct: "Add a temperature for connected (Kanban &
  // List)"). Per column; null keeps the board's own order.
  const [kanbanTempSort, setKanbanTempSort] = useState({ appraisal: null, connected: null, long_term: null });
  const [editLead, setEditLead] = useState(null);

  const [addOpen, setAddOpen] = useState(false);
  // Import straight onto the board (Christian Gravias, Monday 13423268504).
  // null = closed; otherwise the agent's owner lists, for the wizard's
  // "kept under" step.
  const [importLists, setImportLists] = useState(null);
  // "Buyer Match" (DOMA 13374913603): when on, each card shows how many CRM
  // buyers fit it. null counts = not loaded yet.
  const [buyerMatch, setBuyerMatch] = useState(false);
  const [buyerCounts, setBuyerCounts] = useState(null);
  const [broadCounts, setBroadCounts] = useState({});
  const [buyersFor, setBuyersFor] = useState(null);
  const [notesLead, setNotesLead] = useState(null);
  const [notesProperty, setNotesProperty] = useState(null);
  const [dbLead, setDbLead] = useState(null);
  const [dbProperty, setDbProperty] = useState(null);
  // A Lost card filed into the Database stays Lost (it is a contact to keep
  // in touch with, not a lead won back), so it is not actioned off the board.
  const [dbFromLost, setDbFromLost] = useState(false);
  const [ignoreTarget, setIgnoreTarget] = useState(null);
  const [editingProperty, setEditingProperty] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [busyKey, setBusyKey] = useState(null);
  // The card whose "more" menu is open, and where to draw it. The menu is
  // fixed to the viewport so a kanban column's own scroll box can never clip
  // it; it opens upward when the card sits too low for it to fit below.
  const [menu, setMenu] = useState(null); // { key, top | bottom, right }
  // A card dropped on Lost waits here for its reason.
  const [lostTarget, setLostTarget] = useState(null);
  // A listing dropped on Sold waits here for the sale details.
  const [soldTarget, setSoldTarget] = useState(null);
  const [markingSold, setMarkingSold] = useState(false);
  const [agents, setAgents] = useState([]);
  // The lead whose hand-over panel is open. Any signed-in agent can hand a lead
  // to another agent when it doesn't fit their goals (Michael, CRM-2026-00113),
  // the same as the Seller Leads page already allows.
  const [assignLead, setAssignLead] = useState(null);
  // The appraisal or listing whose Change Agent panel is open (the Assigned
  // column on a property row).
  const [assignProperty, setAssignProperty] = useState(null);
  const { user } = useSelector((state) => state.auth || {});
  const canAssignLeads = !!user;
  // Export is for the admin tier only (Christian Gravias, Monday 13423268504:
  // "remove export for pipeline for agents"). Role only, like the agent
  // switcher: some agent accounts carry is_staff.
  const canExport = canIncludeColleagues(user);

  const openImport = async () => {
    const res = await getProspectLists("owners").catch(() => ({ data: [] }));
    setImportLists(Array.isArray(res.data) ? res.data : []);
  };

  // Only the newest load may draw the board. Switching agents quickly starts a
  // second load while the first is still out, and the first one landing last
  // would put the previous agent's cards back on the screen.
  const loadSeq = useRef(0);
  const load = useCallback(async () => {
    const seq = ++loadSeq.current;
    setLoading(true);
    try {
      const data = await loadProspectsPipeline({
        agencyWide,
        canScope: canSwitch,
        boardAgent: boardAgentId,
        team,
        ownOnly,
      });
      if (seq === loadSeq.current) setBoard(data);
    } catch (err) {
      if (seq !== loadSeq.current) return;
      console.error("Error loading the Prospects pipeline:", err);
      toast.error("Failed to load the pipeline.");
      setBoard(EMPTY_BOARD);
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }, [agencyWide, canSwitch, boardAgentId, team, ownOnly]);

  useEffect(() => {
    if (mode === "pipeline") load();
  }, [mode, load]);

  // Close a card's "more" menu when clicking anywhere else, or on any scroll
  // (it is pinned to where the button was).
  useEffect(() => {
    if (!menu) return undefined;
    const close = () => setMenu(null);
    document.addEventListener("click", close);
    window.addEventListener("scroll", close, true);
    return () => {
      document.removeEventListener("click", close);
      window.removeEventListener("scroll", close, true);
    };
  }, [menu]);

  const toggleMenu = (event, key) => {
    if (menu?.key === key) {
      setMenu(null);
      return;
    }
    const r = event.currentTarget.getBoundingClientRect();
    const right = Math.max(8, window.innerWidth - r.right);
    setMenu(
      window.innerHeight - r.bottom > 380
        ? { key, top: r.bottom + 4, right }
        : { key, bottom: window.innerHeight - r.top + 4, right }
    );
  };

  const filtered = useMemo(
    () => board.items.filter((item) => matchesMarket(item, market) && matchesSearch(item, search)),
    [board.items, market, search]
  );

  // One request scores the whole board. It re-runs only when the SET of cards
  // changes (a card added, removed or moved column), not on every edit to a
  // card, and the old counts stay up until the new ones land.
  const matchSignature = useMemo(
    () => board.items.filter(matchable).map(matchKey).sort().join(","),
    [board.items]
  );
  const itemsRef = useRef(board.items);
  itemsRef.current = board.items;
  useEffect(() => {
    if (!buyerMatch) return undefined;
    let cancelled = false;
    fetchBuyerCounts(itemsRef.current)
      .then(({ counts, broad }) => {
        if (cancelled) return;
        setBuyerCounts(counts);
        setBroadCounts(broad);
      })
      .catch(() => {
        if (cancelled) return;
        setBuyerCounts({});
        setBroadCounts({});
        toast.error("Could not match buyers just now.");
      });
    return () => { cancelled = true; };
  }, [buyerMatch, matchSignature]);

  // How many cards have at least one buyer, for the toggle itself, so turning
  // it on visibly answers something wherever the cards are scrolled to.
  const matchedCards = useMemo(
    () => Object.values(buyerCounts || {}).filter((n) => n > 0).length,
    [buyerCounts]
  );

  const renderBuyerChip = (item) => {
    if (!buyerMatch || !matchable(item)) return null;
    if (buyerCounts === null) {
      return <span className="px-1.5 py-0.5 rounded-md bg-gray-100 text-gray-400 text-[10px] font-bold">… buyers</span>;
    }
    const count = buyerCounts[matchKey(item)] || 0;
    // Buyers who named only "Melbourne" are not counted as a match for the
    // area; they show as "+N broad" and in their own section when opened.
    const broad = broadCounts[matchKey(item)] || 0;
    const any = count + broad > 0;
    return (
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setBuyersFor(item); }}
        disabled={!any}
        title={
          !any
            ? "No buyer in the CRM fits this yet"
            : broad
            ? `See the buyers who match. ${broad} more named only the city, not this area.`
            : "See the buyers who match"
        }
        className={`px-1.5 py-0.5 rounded-md text-[10px] font-bold whitespace-nowrap ${
          count ? "bg-[#EDE9FE] text-[#6D28D9] hover:bg-[#DDD6FE]"
            : any ? "bg-gray-100 text-gray-600 hover:bg-gray-200"
            : "bg-gray-100 text-gray-400 cursor-default"
        }`}
      >
        {count} {count === 1 ? "buyer" : "buyers"}
        {broad > 0 && <span className="font-semibold opacity-70"> +{broad} broad</span>}
      </button>
    );
  };

  // Column sizes come from the server's own counts while nothing is filtered,
  // so a column holding more records than it brought back as cards still says
  // how big it really is. Once a market or a search narrows the board, the
  // count is the cards that match.
  const unfiltered = market === "all" && !search.trim();
  const byStage = useMemo(() => {
    const groups = Object.fromEntries(PROSPECT_STAGES.map((s) => [s.id, []]));
    filtered.forEach((item) => groups[item.stage]?.push(item));
    const rank = (i) => TEMPERATURE_BY_ID[i.temperature]?.rank || 0;
    Object.entries(kanbanTempSort).forEach(([stageId, order]) => {
      if (!order) return;
      // Unsorted cards stay at the bottom whichever way it runs.
      groups[stageId] = [...groups[stageId]].sort((a, b) => {
        const ra = rank(a);
        const rb = rank(b);
        if (!ra || !rb) return (rb ? 1 : 0) - (ra ? 1 : 0);
        return order === "hot" ? rb - ra : ra - rb;
      });
    });
    return groups;
  }, [filtered, kanbanTempSort]);
  const countFor = (stageId) => (unfiltered ? board.counts[stageId] || 0 : byStage[stageId].length);
  const atRiskCount = unfiltered ? board.atRisk : byStage.new.filter(isAtRisk).length;
  // Lost is on the board to be tracked, not worked, so it is not in the total.
  const totalCount = PROSPECT_STAGES.filter((s) => isPipelineStage(s.id)).reduce((sum, s) => sum + countFor(s.id), 0);

  // ---- local board edits, so a card moves the moment the action lands ----
  const updateItem = (key, patch) =>
    setBoard((prev) => ({
      ...prev,
      items: prev.items.map((item) => (item.key === key ? { ...item, ...patch(item) } : item)),
    }));

  const removeItem = (item) =>
    setBoard((prev) => ({
      ...prev,
      items: prev.items.filter((i) => i.key !== item.key),
      counts: { ...prev.counts, [item.stage]: Math.max(0, (prev.counts[item.stage] || 0) - 1) },
      atRisk: isAtRisk(item) ? Math.max(0, prev.atRisk - 1) : prev.atRisk,
    }));

  // ---- seller lead actions (the same calls the Seller Leads page makes) ----
  const handleCalled = async (item) => {
    setBusyKey(item.key);
    try {
      await setSellerLeadAction(item.raw.id, "called");
      const now = new Date().toISOString();
      // Called again on a Connected card (Christian Gravias, Monday
      // 12988442847, 16 Oct 2026: "Add Called Notes tiles to Connected like
      // New") records the new call; the card stays where it is.
      if (item.stage === "connected" || item.stage === "long_term") {
        updateItem(item.key, (i) => ({ lastActivityAt: now, raw: { ...i.raw, last_contact_date: now } }));
        invalidateCache.leads();
        toast.success("Call recorded.");
        return;
      }
      setBoard((prev) => ({
        ...prev,
        items: prev.items.map((i) =>
          i.key === item.key
            ? {
                ...i,
                stage: "connected",
                enteredAt: now,
                lastActivityAt: now,
                lastActivity: i.lastActivity === "Not yet called" ? "Called" : i.lastActivity,
                raw: { ...i.raw, last_contact_date: now },
              }
            : i
        ),
        counts: {
          ...prev.counts,
          new: Math.max(0, prev.counts.new - 1),
          connected: prev.counts.connected + 1,
        },
        atRisk: isAtRisk(item) ? Math.max(0, prev.atRisk - 1) : prev.atRisk,
      }));
      invalidateCache.leads();
      toast.success("Moved to Connected.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to mark as contacted.");
    } finally {
      setBusyKey(null);
    }
  };

  // Hands the lead to the Add Appraisal form already filled in. The lead only
  // leaves the board once the appraisal is saved (AddPropertyPage marks it
  // actioned through sellerLeadId), so backing out of the form loses nothing.
  // A lead whose appraisal was moved back to Connected gets that same
  // appraisal back instead, so the property never has two profiles.
  const handleAddAsAppraisal = async (item) => {
    const lead = item.raw;
    const propertyId = typeof lead.property === "object" ? lead.property?.id : lead.property;
    if (propertyId) {
      try {
        await moveLostPropertyToAppraisal(propertyId);
        removeItem(item);
        toast.success("Moved back to Appraisal.");
        invalidateCache.properties();
        invalidateCache.leads();
        load();
        return;
      } catch {
        // Not a card that was moved back; the form it is.
      }
    }
    const parsed = parseAuAddress(item.subtitle);
    navigate("/properties/add-property", {
      state: {
        listingType: "appraisal",
        sellerLeadId: lead.id,
        prefill: {
          address: parsed.address,
          suburb: parsed.suburb,
          state: parsed.state,
          postal_code: parsed.postal_code,
          owner_name: lead.name || lead.full_name || "",
          owner_contact: lead.phone || "",
          owner_email: lead.email || "",
          appraisal_market_type: item.market,
        },
      },
    });
  };

  // Listed is one click, the same call the Seller Leads page makes (Monday
  // 13177615952): the lead's own profile becomes a 'Listed' draft in My
  // Listings, and the card reappears in the Listed column. Only a lead with no
  // address to build a profile from falls back to the Add Property form.
  // Dragged onto For Sale, the new draft goes straight on to a live listing.
  const listLead = (item) => {
    const parsed = parseAuAddress(item.subtitle);
    return markSellerLeadListed(item.raw.id, {
      address: parsed.address,
      suburb: parsed.suburb,
      state: parsed.state,
      postal_code: parsed.postal_code,
    });
  };

  const handleLeadListed = async (item, to = "listed") => {
    setBusyKey(item.key);
    try {
      const { data } = await listLead(item);
      if (to === "for_sale" && data?.property?.id) await movePropertyBack(data.property.id, "for_sale");
      removeItem(item);
      const where = data?.property?.address ? `${data.property.address} moved` : "Moved";
      toast.success(`${where} to ${STAGE_BY_ID[to].label}.`);
      invalidateCache.properties();
      load();
    } catch (err) {
      const body = err?.response?.data;
      if (body?.code === "no_address") openListingForm(item);
      else toast.error(body?.detail || "Failed to mark as listed.");
    } finally {
      setBusyKey(null);
    }
  };

  const openListingForm = (item) => {
    const lead = item.raw;
    const parsed = parseAuAddress(item.subtitle);
    navigate("/properties/add-property", {
      state: {
        listingType: item.market === "on_market" ? "onmarket" : "offmarket",
        sellerLeadId: lead.id,
        prefill: {
          address: parsed.address,
          suburb: parsed.suburb,
          state: parsed.state,
          postal_code: parsed.postal_code,
          owner_name: lead.name || lead.full_name || "",
          owner_contact: lead.phone || "",
          owner_email: lead.email || "",
        },
      },
    });
  };

  const handleIgnore = async (item, reason, notes) => {
    setBusyKey(item.key);
    try {
      await setSellerLeadAction(item.raw.id, "ignored", reason, notes);
      removeItem(item);
      invalidateCache.leads();
      toast.success("Lead ignored.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to ignore the lead.");
    } finally {
      setBusyKey(null);
    }
  };

  // The person on a seller lead is really a buyer: the lead becomes a buyer
  // enquiry for the same agent and moves to Buyer Enquiries (Christian
  // Gravias, DOMA 13423235571: "Add section in pipeline to change vendor to
  // buyer").
  const handleMoveToBuyer = async (item) => {
    if (!window.confirm(`Move ${item.title} to Buyers? The card comes off the pipeline and shows on Buyer Enquiries instead.`)) return;
    setBusyKey(item.key);
    try {
      await setSellerLeadAction(item.raw.id, "buyer");
      removeItem(item);
      invalidateCache.leads();
      toast.success("Moved to Buyer Enquiries.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to move the lead to Buyers.");
    } finally {
      setBusyKey(null);
    }
  };

  const handleLeadLost = async (item, reason, note) => {
    setMenu(null);
    setBusyKey(item.key);
    try {
      await setSellerLeadAction(item.raw.id, "lost", reason, note);
      removeItem(item);
      invalidateCache.leads();
      toast.success("Moved to Lost.");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to mark the lead as lost.");
    } finally {
      setBusyKey(null);
    }
  };

  // The Database picker has saved the record; take the lead off the board.
  const handleLeadFiled = async (lead) => {
    const item = board.items.find((i) => i.kind === "lead" && i.raw.id === lead.id);
    setDbLead(null);
    if (dbFromLost) {
      setDbFromLost(false);
      toast.success("Added to the Database. It stays in Lost.");
      return;
    }
    try {
      await setSellerLeadAction(lead.id, "database");
      if (item) removeItem(item);
      invalidateCache.leads();
    } catch (err) {
      // The record is saved either way, so this is never a failure to report.
      console.error("Failed to mark the seller lead as actioned:", err);
    }
  };

  const handleNoteSaved = (updated) => {
    const notes = Array.isArray(updated?.agent_notes) ? updated.agent_notes : [];
    const last = notes[notes.length - 1];
    const key = `lead-${updated.id}`;
    const card = board.items.find((i) => i.key === key);
    // A note that says "called" is the call (Christian Gravias, Monday
    // 12988442847, 16 Oct 2026: "If the agent writes a note and says called
    // can we make the system mark it as called even if they dont click the
    // button"). The server stamped it; a New card moves to Connected here.
    const calledAt = updated?.last_contact_date;
    const nowCalled = card?.stage === "new" && calledAt && !card.raw?.last_contact_date;
    setBoard((prev) => ({
      ...prev,
      items: prev.items.map((item) =>
        item.key === key
          ? {
              ...item,
              ...(nowCalled ? { stage: "connected", enteredAt: calledAt } : {}),
              raw: { ...item.raw, agent_notes: notes, ...(calledAt ? { last_contact_date: calledAt } : {}) },
              lastActivity: last?.content || item.lastActivity,
              lastActivityAt: last?.created_at || item.lastActivityAt,
            }
          : item
      ),
      counts: nowCalled
        ? { ...prev.counts, new: Math.max(0, prev.counts.new - 1), connected: prev.counts.connected + 1 }
        : prev.counts,
      atRisk: nowCalled && isAtRisk(card) ? Math.max(0, prev.atRisk - 1) : prev.atRisk,
    }));
    if (nowCalled) toast.success("The note says called, so it moved to Connected.");
    setNotesLead((prev) => (prev && prev.id === updated.id ? { ...prev, agent_notes: notes } : prev));
  };

  // The Edit button on a New / Connected card (Christian Gravias, Monday
  // 12988442847, 16 Oct 2026: "Add Edit tile to NEW & CONNECTED like
  // Appraisal"; 17 Oct: "Use the Edit button under Appraisal for New &
  // Connected also"): the Edit Appraisal panel, in its lead form. A note in
  // it that says called moves a New card on, the same as the Notes button.
  const handleLeadEdited = (updated) => {
    setEditLead(null);
    const address = updated.property_address || updated.owner_subject_address || "";
    const name = (updated.name || updated.full_name || "").trim() || "No name";
    updateItem(`lead-${updated.id}`, (item) => ({
      title: name,
      person: name,
      subtitle: address,
      fullAddress: address,
      phone: (updated.phone || "").trim(),
      email: (updated.email || "").trim(),
      market: updated.market === "on_market" ? "on_market" : "off_market",
      temperature: hasTemperature(item) ? String(updated.temperature || "").toLowerCase() : item.temperature,
      raw: { ...item.raw, ...updated },
    }));
    handleNoteSaved(updated);
  };

  // ---- appraisal actions (the same calls the appraisal boards make) ----
  const handleSaveAppraisal = async (propertyId, payload) => {
    setSavingEdit(true);
    try {
      await patchProperty(propertyId, payload);
      toast.success("Appraisal updated");
      setEditingProperty(null);
      invalidateCache.properties();
      load();
    } catch (err) {
      console.error("Error updating appraisal:", err);
      toast.error("Failed to update appraisal");
    } finally {
      setSavingEdit(false);
    }
  };

  const handleAppraisalListed = async (property) => {
    try {
      await markPropertyListed(property.id);
      setEditingProperty(null);
      toast.success("Moved to Listed (a draft in My Listings)");
      invalidateCache.properties();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to mark as listed");
    }
  };

  const handleAppraisalLost = async (property, reason, note) => {
    if (!reason) return;
    try {
      await markPropertyLost(property.id, reason, note);
      setEditingProperty(null);
      toast.success("Moved to Lost");
      invalidateCache.properties();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to mark as lost");
    }
  };

  // "Move to Lost" on a Listed or For Sale card: the listing's own Withdraw,
  // with the listing reasons, so it also comes off REA when the CRM put it
  // there. The card then shows in the Lost column.
  const handleListingLost = async (item, reason, note) => {
    setBusyKey(item.key);
    try {
      await withdrawProperty(item.raw.id, reason, note);
      removeItem(item);
      toast.success("Moved to Lost. The listing is withdrawn.");
      invalidateCache.properties();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to move the listing to Lost.");
    } finally {
      setBusyKey(null);
    }
  };

  // Long Term in and out (Christian Gravias, 20 Oct 2026: "create a category
  // in prospects between connects & appraisals to be a tab called long
  // term"). Parking stamps Lead.long_term_at; taking it out clears it, and a
  // lead that was never called is marked called on the way to Connected.
  const handleLongTerm = async (item, parked) => {
    const lead = item.raw;
    const now = new Date().toISOString();
    setBusyKey(item.key);
    try {
      await updateLead(lead.id, { long_term_at: parked ? now : null });
      const calledNow = !parked && !lead.last_contact_date;
      if (calledNow) await setSellerLeadAction(lead.id, "called");
      const to = parked ? "long_term" : "connected";
      setBoard((prev) => ({
        ...prev,
        items: prev.items.map((i) =>
          i.key === item.key
            ? {
                ...i,
                stage: to,
                enteredAt: parked ? now : lead.last_contact_date || now,
                temperature: String(lead.temperature || "").toLowerCase(),
                lastActivity: i.lastActivity === "Not yet called" || i.lastActivity === "Long term" ? (parked ? "Long term" : "Called") : i.lastActivity,
                raw: { ...i.raw, long_term_at: parked ? now : null, ...(calledNow ? { last_contact_date: now } : {}) },
              }
            : i
        ),
        counts: {
          ...prev.counts,
          [item.stage]: Math.max(0, (prev.counts[item.stage] || 0) - 1),
          [to]: (prev.counts[to] || 0) + 1,
        },
        atRisk: isAtRisk(item) ? Math.max(0, prev.atRisk - 1) : prev.atRisk,
      }));
      invalidateCache.leads();
      toast.success(parked ? "Moved to Long Term." : "Moved back to Connected.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to move the lead.");
    } finally {
      setBusyKey(null);
    }
  };

  // Cold / Warm / Hot straight from the card or the list row (Christian
  // Gravias, Monday 12988442847, 13 Oct 2026: "we need to have a cold warm hot
  // where agents can toggle there properties ... they are all in 1 list just
  // editable"). The same field the Off Market board's columns are built from.
  const handleTemperature = async (item, temperature) => {
    if (!temperature || temperature === item.temperature) return;
    const before = item.temperature;
    // A Connected lead keeps its own temperature on the Lead; an appraisal's
    // is the property's appraisal_temperature.
    const field = item.kind === "lead" ? "temperature" : "appraisal_temperature";
    updateItem(item.key, (i) => ({ temperature, raw: { ...i.raw, [field]: temperature } }));
    try {
      if (item.kind === "lead") {
        await updateLead(item.raw.id, { temperature });
        invalidateCache.leads();
      } else {
        await patchProperty(item.raw.id, { appraisal_temperature: temperature });
        invalidateCache.properties();
      }
      toast.success(`Marked ${TEMPERATURE_BY_ID[temperature]?.label || temperature}.`);
    } catch (err) {
      updateItem(item.key, (i) => ({ temperature: before, raw: { ...i.raw, [field]: before } }));
      toast.error(err?.response?.data?.detail || "Failed to change the temperature.");
    }
  };

  // On market / Off market straight from the card or the list row, as a
  // dropdown like the temperature one (Christian Gravias, Monday 12988442847,
  // 17 Oct 2026: "Please make on market & off market settings changable in
  // kanban & list. Make it a drop down tile like temperature is above it").
  // A lead keeps it on Lead.market; an appraisal on appraisal_market_type,
  // the same field the Edit Appraisal panel's pipeline tiles write. A
  // listing's market is its listing status, so that one is not changed here.
  const handleMarket = async (item, next) => {
    if (!next || next === item.market) return;
    const before = item.market;
    const field = item.kind === "lead" ? "market" : "appraisal_market_type";
    updateItem(item.key, (i) => ({ market: next, raw: { ...i.raw, [field]: next } }));
    try {
      if (item.kind === "lead") {
        await updateLead(item.raw.id, { market: next });
        invalidateCache.leads();
      } else {
        await patchProperty(item.raw.id, { appraisal_market_type: next });
        invalidateCache.properties();
      }
      toast.success(`Moved to ${MARKET_LABEL[next]}.`);
    } catch (err) {
      updateItem(item.key, (i) => ({ market: before, raw: { ...i.raw, [field]: before } }));
      toast.error(err?.response?.data?.detail || "Failed to change the market.");
    }
  };

  // The X on a Lost card: off the board for good, but the lead / appraisal
  // keeps its lost reason and date, so every lost stat still counts it
  // (Christian Gravias, Monday 13177615952).
  const handleHideLost = async (item) => {
    setBusyKey(item.key);
    try {
      await hideLostFromProspects(item.kind === "lead" ? "lead" : "property", item.raw.id);
      removeItem(item);
      toast.success("Removed from Lost. It still counts in your lost stats.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to remove it.");
    } finally {
      setBusyKey(null);
    }
  };

  // The two options under a Lost card's X (Christian Gravias, Monday
  // 12988442847, 14 Oct 2026: "Under the X on lost add 2 settings. Move to
  // Appraisal. Add to Database").
  //
  // Move to Appraisal: a lost lead opens Add Appraisal filled in from it,
  // exactly like the New / Connected cards; saving it un-marks the Lost. A
  // lost appraisal or listing comes straight back onto the Appraisal column.
  const handleLostToAppraisal = async (item) => {
    if (item.kind === "lead") {
      handleAddAsAppraisal(item);
      return;
    }
    setBusyKey(item.key);
    try {
      await moveLostPropertyToAppraisal(item.raw.id);
      removeItem(item);
      toast.success("Moved back to Appraisal.");
      invalidateCache.properties();
      invalidateCache.leads();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to move it back to Appraisal.");
    } finally {
      setBusyKey(null);
    }
  };

  // A property card to New, Connected, Long Term, Appraisal, Listed or For Sale
  // (George Bottas, CRM-2026-00115; Christian Gravias, 21 Oct 2026: "any card
  // can be moved anywhere"). Anything that undoes something live asks first: a
  // listing coming off the website and portals (and REA).
  const moveBackWarning = (item, to) => {
    const label = STAGE_BY_ID[to]?.label || to;
    if (to === "for_sale") return "";
    if (isOnRea(item) && (item.stage === "for_sale" || item.stage === "listed")) {
      return `Move this listing to ${label}? It comes off the website, the buyer portal and realestate.com.au. If it was put on realestate.com.au through Vault, it has to be taken down there too.`;
    }
    if (item.stage === "for_sale") return `Move this listing to ${label}? It comes off the website and buyer portal.`;
    if (to === "long_term") {
      return "Park this as Long Term? The card comes off the pipeline and the seller lead moves to Long Term.";
    }
    return "";
  };

  const handleMoveBack = async (item, to) => {
    const warning = moveBackWarning(item, to);
    if (warning && !window.confirm(warning)) return;
    setBusyKey(item.key);
    try {
      const res = await movePropertyBack(item.raw.id, to);
      removeItem(item);
      toast.success(`Moved to ${STAGE_BY_ID[to]?.label || to}.`);
      if (res?.data?.rea?.still_live) {
        toast("It may still be live on realestate.com.au. Please take it down in Vault.", { duration: 8000 });
      }
      invalidateCache.properties();
      invalidateCache.leads();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to move it.");
    } finally {
      setBusyKey(null);
    }
  };

  // A double-up appraisal can be deleted off the board (Genneva Smarrelli,
  // Monday CRM-2026-00119: "Double ups in the crm can not be deleted"). It
  // goes to Property Trash like a delete from My Listings, so it can be
  // restored for 180 days. Its seller lead stays actioned, so no lead card
  // comes back in its place.
  const handleDeleteAppraisal = async (item) => {
    if (!window.confirm(`Delete the appraisal for ${item.title}? It goes to Property Trash and can be restored from there.`)) return;
    setBusyKey(item.key);
    try {
      await deleteProperty(item.raw.id);
      removeItem(item);
      toast.success("Appraisal deleted.");
      invalidateCache.properties();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to delete the appraisal.");
    } finally {
      setBusyKey(null);
    }
  };

  // A lead card to New, out of Lost, or anywhere in the holding area the
  // Called / Long Term buttons don't already cover.
  const handleLeadBoardMove = async (item, to) => {
    setBusyKey(item.key);
    try {
      await moveLeadOnBoard(item.raw.id, to);
      removeItem(item);
      toast.success(`Moved to ${STAGE_BY_ID[to]?.label || to}.`);
      invalidateCache.leads();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to move the lead.");
    } finally {
      setBusyKey(null);
    }
  };

  const handleLostToDatabase = (item) => {
    if (item.kind === "lead") {
      setDbFromLost(true);
      setDbLead(item.raw);
    } else {
      setDbProperty(item.raw);
    }
  };

  const openItem = (item) => {
    if (item.kind === "lead") setNotesLead(item.raw);
    else navigate(`/properties/${item.raw.id}`);
  };

  // The Last Activity cell always opens Agent Notes, for every row kind, so the
  // behaviour is consistent across tabs (DOMA 13343247771). Lead rows use the
  // lead modal; property rows use the property notes modal. The rest of the row
  // keeps openItem (property rows still navigate to the listing).
  const openNotes = (item) => {
    if (item.kind === "lead") setNotesLead(item.raw);
    else setNotesProperty(item.raw);
  };

  const confirmLost = (item, reason, note) => {
    setLostTarget(null);
    if (!item || !reason) return;
    if (item.kind === "lead") handleLeadLost(item, reason, note);
    else if (isListing(item)) handleListingLost(item, reason, note);
    else handleAppraisalLost(item.raw, reason, note);
  };

  // Mark as Sold, the same fields the property page sends (handleToggleSold in
  // PropertyDetailPage), so a sale recorded from the board is the same sale.
  const openSold = (item) => {
    setSoldTarget(item);
    if (!agents.length) {
      getAgents({ page_size: 200 })
        .then((res) => setAgents(res?.data?.results ?? (Array.isArray(res?.data) ? res.data : [])))
        .catch(() => setAgents([]));
    }
  };

  // Hand a seller lead to an agent, through the same modal Seller Leads uses:
  // the agent picker, an optional note, and the one assignment email the backend
  // sends only when the lead moves to a different agent. The picker's own
  // server-side search drives the list; the loaded agents just seed its options.
  // Opened from the ⋯ menu and, on a New row in the list, from the Assign field
  // under the stage (Christian Gravias, Monday 12988442847, 17 Oct 2026: "Under
  // New - Stage add an assign to agent clickable field where super admin can
  // choose to assign an agent like we had it on the old CRM ... Also leave it
  // under settings").
  const openAssignLead = (item) => {
    if (item.kind === "property") setAssignProperty(item);
    else setAssignLead(item.raw);
    if (!agents.length) {
      getAgents({ page_size: 200 })
        .then((res) => setAgents(res?.data?.results ?? (Array.isArray(res?.data) ? res.data : [])))
        .catch(() => setAgents([]));
    }
  };

  // Reassigning changes who owns the lead and, on a scoped board, whether it
  // still belongs here at all, so reload rather than patch the row in place.
  const handleLeadAssigned = () => {
    setAssignLead(null);
    load();
  };

  const confirmSold = async (details) => {
    const item = soldTarget;
    if (!item) return;
    setMarkingSold(true);
    try {
      // Sold from anywhere: a lead is Listed first (its profile is the sale),
      // and anything not already a live listing becomes one, so the sale sits
      // in Sold alone rather than in Appraisal / Listed / Lost as well.
      let propertyId = item.kind === "property" ? item.raw.id : null;
      if (item.kind === "lead") {
        const { data: listed } = await listLead(item);
        propertyId = listed?.property?.id;
        if (!propertyId) throw new Error("no property");
      }
      if (item.stage !== "for_sale") await movePropertyBack(propertyId, "for_sale");
      const data = {
        is_sold: true,
        sold_price: details.soldPrice,
        suppress_sold_price: details.suppressSoldPrice,
        sold_date: details.soldDate || moment().format("YYYY-MM-DD"),
        commission_co_agent_id: details.coAgentId || "",
        commission_split_percentage: details.splitPercentage,
        no_commission_credit: details.noCommissionCredit ? "true" : "false",
      };
      if (details.settlementDate) data.settlement_date = details.settlementDate;
      if (Array.isArray(details.purchasers) && details.purchasers.length) {
        data.purchasers = JSON.stringify(details.purchasers);
      }
      await patchProperty(propertyId, data);
      setSoldTarget(null);
      toast.success("Moved to Sold.");
      invalidateCache.properties();
      invalidateCache.leads();
      load();
    } catch (err) {
      const body = err?.response?.data;
      if (body?.code === "no_address") {
        setSoldTarget(null);
        toast("Add the property address first, then mark it Sold.");
        openListingForm(item);
      } else {
        toast.error(body?.detail || "Failed to mark as sold.");
      }
    } finally {
      setMarkingSold(false);
    }
  };

  // One move, from a drag on the kanban or the Stage picker on the list. Any
  // card to any column but a Sold one; each target runs the action that column
  // stands for (see allowedMoves above).
  const moveItem = (item, to) => {
    if (!item || !to || item.stage === to) return;
    if (isSold(item)) {
      toast("A sold property stays in Sold.");
      return;
    }
    if (to === "lost") {
      setLostTarget(item);
      return;
    }
    if (to === "sold") {
      openSold(item);
      return;
    }
    if (item.kind === "lead") {
      const inHoldingArea = item.stage !== "lost";
      if (to === "appraisal") handleAddAsAppraisal(item);
      else if (to === "listed" || to === "for_sale") handleLeadListed(item, to);
      else if (inHoldingArea && to === "long_term") handleLongTerm(item, true);
      else if (inHoldingArea && to === "connected" && item.stage === "long_term") handleLongTerm(item, false);
      else if (inHoldingArea && to === "connected") handleCalled(item);
      else handleLeadBoardMove(item, to);
      return;
    }
    if (to === "listed" && item.stage === "appraisal") handleAppraisalListed(item.raw);
    else handleMoveBack(item, to);
  };

  const handleDragEnd = ({ draggableId, source, destination }) => {
    if (!destination || destination.droppableId === source.droppableId) return;
    const item = board.items.find((i) => i.key === draggableId);
    moveItem(item, destination.droppableId);
  };

  // ---- list view ----
  const stageOrder = Object.fromEntries(PROSPECT_STAGES.map((s, i) => [s.id, i]));
  const SORTERS = {
    title: (i) => i.title.toLowerCase(),
    stage: (i) => stageOrder[i.stage],
    // Hot first on the first click, Cold first on the second.
    temperature: (i) => -(TEMPERATURE_BY_ID[i.temperature]?.rank || 0),
    market: (i) => i.market || "",
    potential: (i) => i.potential,
    timeframe: (i) => (i.timeframe || "").toLowerCase(),
    days: (i) => daysSince(i.enteredAt) ?? -1,
    activity: (i) => (i.lastActivityAt ? moment(i.lastActivityAt).valueOf() : 0),
    assigned: (i) => (i.assigned || "").toLowerCase(),
    source: (i) => i.source || "",
  };
  const listRows = useMemo(() => {
    const rows = filtered.filter((i) => stageFilter === "all" || i.stage === stageFilter);
    const get = SORTERS[sort.key] || SORTERS.stage;
    const dir = sort.dir === "desc" ? -1 : 1;
    return [...rows].sort((a, b) => {
      // Sorting by temperature lines the appraisals up; everything without
      // one (leads, listings) stays underneath whichever way it runs.
      if (sort.key === "temperature") {
        const ta = hasTemperature(a) ? 0 : 1;
        const tb = hasTemperature(b) ? 0 : 1;
        if (ta !== tb) return ta - tb;
      }
      const x = get(a);
      const y = get(b);
      if (x < y) return -1 * dir;
      if (x > y) return 1 * dir;
      return stageOrder[a.stage] - stageOrder[b.stage];
    });
    // SORTERS and stageOrder are rebuilt every render from constants.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered, stageFilter, sort]);

  const toggleSort = (key) =>
    setSort((prev) => (prev.key === key ? { key, dir: prev.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));

  // ---- export ----
  const handleExport = () => {
    const rows = view === "list" ? listRows : filtered;
    if (!rows.length) {
      toast("Nothing to export.");
      return;
    }
    const header = ["Lead / Property", "Address", "Stage", "Temperature", "Market", "Selling Time Frame", "Potential commission", "Days in stage", "Last activity", "Assigned", "Source"];
    const csvCell = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = [header.map(csvCell).join(",")];
    rows.forEach((i) => {
      lines.push(
        [
          i.title,
          i.subtitle,
          STAGE_BY_ID[i.stage]?.label,
          TEMPERATURE_BY_ID[i.temperature]?.label || "",
          MARKET_LABEL[i.market] || "",
          i.timeframe || "",
          i.potential ? Math.round(i.potential) : "",
          daysSince(i.enteredAt) ?? "",
          i.lostReason || i.lastActivity,
          i.assigned,
          i.source,
        ]
          .map(csvCell)
          .join(",")
      );
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `prospects-${moment().format("YYYY-MM-DD")}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  // ---- column sub-lines: velocity, not just a total ----
  const columnFoot = (stageId) => {
    const items = byStage[stageId];
    const money = items.reduce((sum, i) => sum + (i.potential || 0), 0);
    if (stageId === "new") {
      return atRiskCount > 0
        ? { text: `${atRiskCount} > 24h old`, alert: true }
        : { text: "None waiting over 24h" };
    }
    if (stageId === "connected") {
      const ages = items.map((i) => daysSince(i.enteredAt)).filter((d) => d !== null);
      if (!ages.length) return { text: "Nobody called yet" };
      const avg = ages.reduce((a, b) => a + b, 0) / ages.length;
      return { text: `~${avg.toFixed(1)}d since called` };
    }
    if (stageId === "long_term") {
      return { text: items.length ? "Not selling soon, keep in touch" : "Nobody parked here" };
    }
    if (stageId === "appraisal") {
      const hot = items.filter((i) => i.temperature === "hot").length;
      return money > 0 ? { text: `${formatMoney(money)} potential` } : { text: `${hot} hot` };
    }
    if (stageId === "listed") {
      return { text: "Not on the portals yet" };
    }
    if (stageId === "for_sale") {
      return money > 0 ? { text: `${formatMoney(money)} comm.` } : { text: `${items.length} live` };
    }
    if (stageId === "lost") {
      return { text: "X removes a card, stats are kept" };
    }
    return money > 0
      ? { text: `${formatMoney(money)} comm., last ${SOLD_WINDOW_DAYS} days` }
      : { text: `Last ${SOLD_WINDOW_DAYS} days` };
  };

  // ---------------------------------------------------------------------------
  // Cards
  // ---------------------------------------------------------------------------
  const marketPill = (value) =>
    market === "all" && MARKET_LABEL[value] ? (
      <span className="inline-block px-1.5 py-0.5 rounded-md bg-gray-100 text-gray-600 text-[10px] font-bold">
        {MARKET_LABEL[value]}
      </span>
    ) : null;

  // A New / Connected lead and an appraisal can be moved between the markets.
  const canChangeMarket = (item) =>
    (item?.kind === "lead" && item.stage !== "lost") || (item?.kind === "property" && item.stage === "appraisal");

  // On market / Off market as a dropdown, styled like the temperature one
  // that sits above it. Shown whatever the market chip, so it can always be
  // changed; everything else keeps the plain pill.
  const renderMarket = (item, { list = false } = {}) => {
    if (!canChangeMarket(item)) {
      return list ? MARKET_LABEL[item.market] || "Not set" : marketPill(item.market);
    }
    return (
      <select
        aria-label={`Market for ${item.title}`}
        value={item.market || ""}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => handleMarket(item, e.target.value)}
        title="Move to On market / Off market"
        className={`${list ? "px-2 py-1 text-[11px]" : "px-1.5 py-0.5 text-[10px]"} rounded-md font-bold border-0 cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#55C5D0] ${
          item.market === "on_market" ? "bg-[#EBFFF2] text-[#15A34A]" : item.market ? "bg-[#E3F8FA] text-[#1B9AA6]" : "bg-gray-100 text-gray-500"
        }`}
      >
        {!item.market && <option value="">Not set</option>}
        <option value="off_market">Off market</option>
        <option value="on_market">On market</option>
      </select>
    );
  };

  // Name, full address, mobile and email, never cut off, on every card in
  // every column, so the agent can ring from wherever the card sits (Christian
  // Gravias, Monday 12988442847, 1 Oct 2026). Tapping the number dials it.
  const renderContact = (item) => {
    const stop = (e) => e.stopPropagation();
    return (
      <div className="mt-1.5 space-y-0.5 text-[11.5px]">
        {item.phone ? (
          <a href={`tel:${item.phone.replace(/\s+/g, "")}`} onClick={stop} className="flex items-start gap-1.5 text-gray-700 hover:text-[#2b9aa4] break-all">
            <Phone className="w-3 h-3 mt-0.5 shrink-0" /> {item.phone}
          </a>
        ) : (
          <p className="flex items-start gap-1.5 text-gray-400">
            <Phone className="w-3 h-3 mt-0.5 shrink-0" /> No mobile on file
          </p>
        )}
        {item.email ? (
          <a href={`mailto:${item.email}`} onClick={stop} className="flex items-start gap-1.5 text-gray-700 hover:text-[#2b9aa4] break-all">
            <Mail className="w-3 h-3 mt-0.5 shrink-0" /> {item.email}
          </a>
        ) : (
          <p className="flex items-start gap-1.5 text-gray-400">
            <Mail className="w-3 h-3 mt-0.5 shrink-0" /> No email on file
          </p>
        )}
      </div>
    );
  };

  // The card's own actions. The kanban card's ⋯ and the list row's ⋯ are the
  // same menu (Christian Gravias, Monday 12988442847: "In Kanban it has these
  // options ... Please add them there for list also").
  const menuEntry = (key, icon, label, onClick, extra = "") => (
    <button
      key={key}
      type="button"
      onClick={() => {
        setMenu(null);
        onClick();
      }}
      className={`w-full text-left px-3 py-1.5 hover:bg-gray-50 flex items-center gap-2 ${extra}`}
    >
      {icon}
      {label}
    </button>
  );

  const menuEntries = (item) => {
    // Three blocks: the card's own actions, then one "move" per stage in the
    // pipeline's order, then the red ones. A curated move (Add as Appraisal,
    // Listed, Back to Connected, Mark as Sold ...) takes its stage's slot and
    // the stages nothing curates get a plain "Move to ..." (Christian
    // Gravias, DOMA 13352447099 + 13362678936: "add all stage options into
    // all the stages tabs"; "Move long term to be under connected, move add
    // as appraisal under long term, move Listed under appraisal").
    const actions = [];
    const moves = {};
    const danger = [];
    const move = (stage, entry) => {
      moves[stage] = entry;
    };
    if (item.kind === "lead" && item.stage !== "lost") {
      // The card's buttons are in the menu too (Christian Gravias, Monday
      // 12988442847, 16 Oct 2026: "For Kanban & List. Add tiles to the
      // settings button: Add Appraisal, Called, Notes, Edit").
      move("appraisal", menuEntry("appraisal", <Home className="w-3 h-3" />, "Add as Appraisal", () => handleAddAsAppraisal(item)));
      actions.push(menuEntry("called", <Phone className="w-3 h-3" />, "Called", () => handleCalled(item)));
      actions.push(menuEntry("notes", <StickyNote className="w-3 h-3" />, "Notes", () => setNotesLead(item.raw)));
      actions.push(menuEntry("edit-lead", <Pencil className="w-3 h-3" />, "Edit", () => setEditLead(item)));
      if (item.stage === "long_term") {
        move("connected", menuEntry("long-term", <Hourglass className="w-3 h-3" />, "Back to Connected", () => handleLongTerm(item, false)));
      } else {
        move("long_term", menuEntry("long-term", <Hourglass className="w-3 h-3" />, "Move to Long Term", () => handleLongTerm(item, true)));
      }
      // Super admin can hand the lead to another agent from here (both the
      // desktop list and the mobile cards share this menu).
      if (canAssignLeads) {
        actions.push(menuEntry("assign", <UserPlus className="w-3 h-3" />, "Assign to agent", () => openAssignLead(item)));
      }
      actions.push(<div key="sep-lead" className="border-t border-gray-100 my-1" />);
      actions.push(menuEntry("ignore", <EyeOff className="w-3 h-3" />, "Ignore", () => setIgnoreTarget(item)));
      move("listed", menuEntry("listed", <CheckCircle2 className="w-3 h-3" />, "Listed", () => handleLeadListed(item)));
      actions.push(menuEntry("db", <DatabaseIcon className="w-3 h-3" />, "Add to Database", () => setDbLead(item.raw)));
      actions.push(menuEntry("to-buyer", <Users className="w-3 h-3" />, "Move to Buyers", () => handleMoveToBuyer(item)));
    } else if (item.stage === "appraisal") {
      actions.push(menuEntry("edit", <Pencil className="w-3 h-3" />, "Edit appraisal", () => setEditingProperty(item.raw)));
      move("listed", menuEntry("listed", <CheckCircle2 className="w-3 h-3" />, "Listed", () => handleAppraisalListed(item.raw)));
      actions.push(menuEntry("db", <DatabaseIcon className="w-3 h-3" />, "Add to Database", () => setDbProperty(item.raw)));
      move("connected", menuEntry("back-connected", <Undo2 className="w-3 h-3" />, "Back to Connected", () => handleMoveBack(item, "connected")));
      move("long_term", menuEntry("long-term", <Hourglass className="w-3 h-3" />, "Move to Long Term", () => handleMoveBack(item, "long_term")));
    } else if (item.kind === "property") {
      actions.push(menuEntry("open", <ExternalLink className="w-3 h-3" />, "Open listing", () => openItem(item)));
      // A Listed draft goes live from its own menu too, not only by dragging
      // (Genneva Smarrelli, CRM-2026-00120: "can't be move in for sale or
      // marked for sale").
      if (item.stage === "listed") {
        move("for_sale", menuEntry("for-sale", <Tag className="w-3 h-3" />, "Move to For Sale", () => handleMoveBack(item, "for_sale")));
      }
      if (item.stage === "listed" || item.stage === "for_sale") {
        move("sold", menuEntry("sold", <CheckCircle2 className="w-3 h-3" />, "Mark as Sold", () => openSold(item)));
        move("appraisal", menuEntry("back-appraisal", <Undo2 className="w-3 h-3" />, "Back to Appraisal", () => handleMoveBack(item, "appraisal")));
        move("connected", menuEntry("back-connected", <Undo2 className="w-3 h-3" />, "Back to Connected", () => handleMoveBack(item, "connected")));
      }
    }
    if (item.stage === "lost") {
      move("appraisal", menuEntry("to-appraisal", <Home className="w-3 h-3" />, "Move to Appraisal", () => handleLostToAppraisal(item)));
      actions.push(menuEntry("lost-db", <DatabaseIcon className="w-3 h-3" />, "Add to Database", () => handleLostToDatabase(item)));
      if (item.kind === "lead") {
        actions.push(menuEntry("notes", <StickyNote className="w-3 h-3" />, "Notes", () => setNotesLead(item.raw)));
      }
      danger.push(menuEntry("hide", <X className="w-3 h-3" />, "Remove from Lost", () => handleHideLost(item), "text-rose-600"));
    }
    // Each stage once, in the pipeline's order; the ones nothing above
    // curates run the same action as a drag or the list's Stage picker.
    const allowed = allowedMoves(item);
    const stageMoves = PROSPECT_STAGES.map((s) => s.id)
      .filter((id) => id !== "lost" && allowed.includes(id))
      .map(
        (id) =>
          moves[id] ||
          menuEntry(`move-${id}`, <ArrowRight className="w-3 h-3" />, `Move to ${STAGE_BY_ID[id].label}`, () => moveItem(item, id))
      );
    // One Lost entry that asks why, rather than every reason in the menu
    // (Christian Gravias, Monday 12988442847, 13 Oct 2026: "Make the Lost to be
    // just 1 button and if its clicked then it brings up the options").
    if (allowed.includes("lost")) {
      danger.push(menuEntry("lost", <XCircle className="w-3 h-3" />, "Move to Lost", () => setLostTarget(item), "text-rose-600"));
    }
    if (item.stage === "appraisal") {
      danger.push(
        menuEntry("delete", <Trash2 className="w-3 h-3" />, "Delete appraisal", () => handleDeleteAppraisal(item), "text-rose-600")
      );
    }
    const entries = [...actions];
    if (stageMoves.length) {
      if (entries.length) entries.push(<div key="sep-moves" className="border-t border-gray-100 my-1" />);
      entries.push(...stageMoves);
    }
    if (danger.length) {
      if (entries.length) entries.push(<div key="sep" className="border-t border-gray-100 my-1" />);
      entries.push(...danger);
    }
    return entries;
  };

  const renderMenu = (item) => (
    <div className="relative" onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        aria-label="More actions"
        onClick={(e) => toggleMenu(e, item.key)}
        className="p-1.5 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50"
      >
        <MoreHorizontal className="w-3.5 h-3.5" />
      </button>
      {menu?.key === item.key && (
        <div
          className="fixed z-50 w-52 max-h-[70vh] overflow-y-auto bg-white border border-gray-200 rounded-xl shadow-xl py-1 text-xs"
          style={{ top: menu.top, bottom: menu.bottom, right: menu.right }}
        >
          {menuEntries(item)}
        </div>
      )}
    </div>
  );

  // The latest note in full, in its own scroll box, so the agent can read it
  // on the card without opening Edit (Christian Gravias, Monday 12988442847,
  // 13 Oct 2026: "Make the note scrollable for the agent to read without
  // having to click edit"). Scrolling it never opens the card underneath.
  const renderNote = (text) => (
    <div
      aria-label="Latest note"
      onClick={(e) => e.stopPropagation()}
      className="mt-2 text-xs text-gray-600 bg-gray-50 rounded-lg px-2.5 py-2 max-h-24 overflow-y-auto custom-scrollbar whitespace-pre-line break-words cursor-text"
    >
      {text}
    </div>
  );

  // Cold / Warm / Hot on an appraisal card, as a pill the agent can change.
  const renderTemperature = (item) => {
    const t = TEMPERATURE_BY_ID[item.temperature];
    return (
      <select
        aria-label={`Temperature for ${item.title}`}
        value={item.temperature || ""}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => handleTemperature(item, e.target.value)}
        title="Move to Cold / Warm / Hot"
        className={`px-1.5 py-0.5 rounded-md text-[10px] font-bold border-0 cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#55C5D0] ${
          t ? t.tag : "bg-gray-100 text-gray-500"
        }`}
      >
        {!t && <option value="">Unsorted</option>}
        {TEMPERATURES.map((x) => (
          <option key={x.id} value={x.id}>
            {x.label}
          </option>
        ))}
      </select>
    );
  };

  /*
   * Every card in every column reads in the same order, so an agent scanning
   * the board never hunts for a line (Christian Gravias, Monday 12988442847,
   * 14 Oct 2026: "Make the text line up the same ... I like under connected
   * it says Called 9 days ago. Please do this for the appraisal section and
   * put the text in the same spot. Put the name of the agent in the same spot
   * across every stage tile"):
   *
   *   name + address        tags
   *   mobile, email
   *   latest note
   *   (Expected Commission, on property cards)
   *   status line           agent        <- same row on every card
   *   buttons
   */
  const statusTextOf = (item) => {
    const p = item.raw || {};
    const ago = (d) => (d && moment(d).isValid() ? moment(d).fromNow() : "");
    switch (item.stage) {
      case "new":
        return `Added ${formatMelbourneShortDate(p.created_at)}, not yet called`;
      case "connected":
        return p.last_contact_date ? `Called ${ago(p.last_contact_date)}` : "Called";
      case "long_term":
        return p.long_term_at ? `Long term since ${formatMelbourneShortDate(p.long_term_at)}` : "Long term";
      case "appraisal":
        return `Appraisal added ${ago(p.created_at)}`;
      case "listed":
        return `Listed ${ago(item.enteredAt)}, not on portals`;
      case "for_sale": {
        const days = daysSince(item.enteredAt);
        return days !== null ? `Live ${days} day${days === 1 ? "" : "s"}` : "Live";
      }
      case "sold":
        return p.sold_date ? `Sold ${formatMelbourneShortDate(p.sold_date)}` : "Settled";
      case "lost":
        return item.enteredAt ? `Lost ${formatMelbourneShortDate(item.enteredAt)}` : "Lost";
      default:
        return "";
    }
  };

  const renderStatusRow = (item) => (
    // The status line is never cut off ("Called 9 days ago" has to be read in
    // full); a long agent name is what gives way.
    <div className="mt-2 flex items-start justify-between gap-2 text-[11px]">
      <span className={`min-w-0 break-words ${isAtRisk(item) ? "text-rose-500 font-semibold" : "text-gray-500"}`}>
        {statusTextOf(item)}
      </span>
      {/* The agent name on a card changes the agent, same as the Assigned
          column in the list (Christian Gravias, 21 Oct 2026, CRM-2026-00113:
          "where it says Spiros Vamvalis in Kan Ban please make this clickable
          to be assigned"). */}
      {canAssignLeads ? (
        <button
          type="button"
          disabled={busyKey === item.key}
          onClick={(e) => {
            e.stopPropagation();
            openAssignLead(item);
          }}
          onKeyDown={(e) => e.stopPropagation()}
          aria-label={`Change agent for ${item.title}`}
          title="Change agent"
          className="shrink-0 max-w-[45%] flex items-center gap-0.5 text-gray-400 hover:text-[#2b9aa4] disabled:opacity-60"
        >
          <span className="truncate">{item.assigned || "Unassigned"}</span>
          <ChevronDown className="w-3 h-3 shrink-0" />
        </button>
      ) : (
        <span className="shrink-0 text-gray-400 truncate max-w-[45%] text-right" title={item.assigned || ""}>
          {item.assigned || "Unassigned"}
        </span>
      )}
    </div>
  );

  // Name and full address on the left, the card's tags stacked on the right.
  const renderCardHead = (item, name, tags) => (
    <div className="flex items-start justify-between gap-2">
      <div className="min-w-0">
        {name}
        <p className="text-xs text-gray-500 break-words">{item.fullAddress || item.subtitle || "No address yet"}</p>
        {item.databaseRecord && <PublicPropertyLink record={item.databaseRecord} className="mt-1 mr-1" />}
        {item.propertyProfile && <PropertyProfileLink profile={item.propertyProfile} className="mt-1 mr-1" />}
        {item.kind === "lead" && (
          <UpdatePropertyInfoButton leadId={item.raw?.id} address={item.fullAddress} className="mt-1" />
        )}
      </div>
      <div className="flex flex-col items-end gap-1 shrink-0">{tags}</div>
    </div>
  );

  const cardShell = "bg-white rounded-xl border border-gray-200 border-l-4 p-3 shadow-sm";

  const renderLeadCard = (item) => {
    const lead = item.raw;
    const stage = STAGE_BY_ID[item.stage];
    const notes = Array.isArray(lead.agent_notes) ? lead.agent_notes : [];
    const lastNote = notes[notes.length - 1];
    const busy = busyKey === item.key;
    return (
      <div role="article" aria-label={item.title} className={cardShell} style={{ borderLeftColor: stage.color }}>
        {renderCardHead(
          item,
          <ContactLink
            identity={{
              contactId: lead.contact_id,
              buyerId: lead.buyer_id,
              email: lead.email,
              name: lead.name || lead.full_name,
              phone: lead.phone,
            }}
            className="font-bold text-[13px] text-gray-900 text-left hover:text-[#3a99a3] hover:underline break-words"
          >
            {item.title}
          </ContactLink>,
          <>
            <LeadSourceBadge source={item.source} />
            <RentalLeadPill transactionType={item.transactionType} />
            {renderBuyerChip(item)}
            {hasTemperature(item) && renderTemperature(item)}
            {renderMarket(item)}
          </>
        )}
        {renderContact(item)}
        {lastNote?.content && renderNote(lastNote.content)}
        {renderStatusRow(item)}
        {/* Two rows so nothing wraps or clips in a kanban column: the main
            move and the menu on top, the quick answers underneath. Notes is
            on the card and Ignore is in the menu (Christian Gravias, Monday
            12988442847: "Please swap Notes with Ignore on the main page"). */}
        <div className="mt-2.5 space-y-1.5">
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              disabled={busy}
              onClick={() => handleAddAsAppraisal(item)}
              className="flex-1 px-2 py-1.5 rounded-lg text-white text-[11px] font-bold flex items-center justify-center gap-1 whitespace-nowrap disabled:opacity-60"
              style={{ background: TEAL }}
            >
              <Home className="w-3 h-3" /> Add as Appraisal
            </button>
            {renderMenu(item)}
          </div>
          {/* Called and Notes on Connected too, and Edit underneath, the same
              as an Appraisal card (Christian Gravias, Monday 12988442847, 16
              Oct 2026: "Add Called Notes tiles to Connected like New" and
              "Add Edit tile to NEW & CONNECTED like Appraisal. Add it under
              Called & Notes"). */}
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              disabled={busy}
              onClick={() => handleCalled(item)}
              title={
                item.stage === "new"
                  ? "I have spoken to them. Moves the lead to Connected."
                  : "I have spoken to them again. Records the new call."
              }
              className="flex-1 px-2 py-1.5 rounded-lg border border-gray-200 text-gray-700 text-[11px] font-bold flex items-center justify-center gap-1 hover:bg-gray-50 disabled:opacity-60"
            >
              <Phone className="w-3 h-3" /> Called
            </button>
            <button
              type="button"
              onClick={() => setNotesLead(lead)}
              className="flex-1 px-2 py-1.5 rounded-lg border border-gray-200 text-gray-700 text-[11px] font-bold flex items-center justify-center gap-1 hover:bg-gray-50"
            >
              <StickyNote className="w-3 h-3" /> Notes{notes.length ? ` (${notes.length})` : ""}
            </button>
          </div>
          <button
            type="button"
            onClick={() => setEditLead(item)}
            className="w-full px-2 py-1.5 rounded-lg border border-gray-200 text-gray-700 text-[11px] font-bold flex items-center justify-center gap-1 hover:bg-gray-50"
          >
            <Pencil className="w-3 h-3" /> Edit
          </button>
        </div>
      </div>
    );
  };

  // A card in the Lost column: what was lost and why, the X, and under it the
  // ⋯ with Move to Appraisal and Add to Database.
  const renderLostCard = (item) => {
    const busy = busyKey === item.key;
    return (
      <div role="article" aria-label={item.title} className={cardShell} style={{ borderLeftColor: STAGE_BY_ID.lost.color }}>
        {renderCardHead(
          item,
          <button type="button" onClick={() => openItem(item)} className="text-left">
            <p className="font-bold text-[13px] text-gray-900 hover:text-[#3a99a3] hover:underline break-words">
              {item.person || item.title}
            </p>
          </button>,
          <>
            <button
              type="button"
              disabled={busy}
              onClick={() => handleHideLost(item)}
              aria-label={`Remove ${item.title} from Lost`}
              title="Remove from your Prospects. It still counts in your lost stats."
              className="p-1 rounded-md text-gray-400 hover:bg-rose-50 hover:text-rose-600 disabled:opacity-50"
            >
              <X className="w-4 h-4" />
            </button>
            {renderMenu(item)}
          </>
        )}
        <div className="mt-2 flex items-center gap-1.5 flex-wrap text-[11px]">
          <span className="px-1.5 py-0.5 rounded-md font-bold bg-rose-50 text-rose-600">{item.lostReason || "Lost"}</span>
          {marketPill(item.market)}
        </div>
        {renderStatusRow(item)}
      </div>
    );
  };

  const renderPropertyCard = (item) => {
    const p = item.raw;
    const stage = STAGE_BY_ID[item.stage];
    const temp = TEMPERATURE_BY_ID[item.temperature];
    const price = parseFloat(p.sold_price) || parseFloat(p.price) || 0;
    let tag = null;
    if (item.stage === "appraisal") {
      tag = renderTemperature(item);
    } else if (item.stage === "sold") {
      tag = <span className="px-1.5 py-0.5 rounded-md text-[10px] font-bold bg-emerald-50 text-emerald-700">Sold</span>;
    }
    const priceText =
      item.stage === "appraisal" ? (price ? `Est. ${formatMoney(price)}` : "No estimate yet") : price ? formatMoney(price) : "";
    // Expected Commission on every card, coloured like the live CRM's
    // appraisal boards: blue Cold, orange Warm, green Hot (Christian Gravias,
    // Monday 12988442847, 13 Oct 2026: "Add Expected Comm to the tile ... Use
    // the colours like we have on our current crm"). Listings and sales,
    // which have no temperature, use the brand teal.
    const strip = temp
      ? { bg: temp.strip, value: temp.value }
      : item.stage === "appraisal"
      ? { bg: "bg-[#F3F4F6]", value: "text-[#6B7280]" }
      : { bg: "bg-[#E6F7F9]", value: "text-[#0e7d86]" };
    return (
      <div
        role="button"
        tabIndex={0}
        aria-label={item.title}
        onClick={() => openItem(item)}
        onKeyDown={(e) => e.key === "Enter" && openItem(item)}
        className={`${cardShell} cursor-pointer hover:shadow-md transition-shadow`}
        style={{ borderLeftColor: stage.color }}
      >
        {renderCardHead(
          item,
          <p className={`font-bold text-[13px] break-words ${item.person ? "text-gray-900" : "text-gray-400"}`}>
            {item.person || "No owner on file"}
          </p>,
          <>
            {tag}
            {renderMarket(item)}
            {renderBuyerChip(item)}
          </>
        )}
        {renderContact(item)}
        {item.lastActivity && renderNote(item.lastActivity)}
        <div className={`mt-2 ${strip.bg} py-2 px-2.5 rounded-lg`}>
          <div className="flex items-center justify-between gap-2">
            <span className="text-[#4B5563] font-medium text-[10px] whitespace-nowrap">
              {item.stage === "sold" ? "Commission" : "Expected Commission"}
            </span>
            <span className={`font-bold text-[11px] whitespace-nowrap ${strip.value}`}>
              {item.potential > 0 ? fullMoney(item.potential) : "Not set"}
            </span>
          </div>
          {priceText && (
            <div className="mt-0.5 flex items-center justify-between gap-2">
              <span className="text-[#4B5563] font-medium text-[10px] whitespace-nowrap">
                {item.stage === "sold" ? "Sold price" : "Price"}
              </span>
              <span className="font-bold text-[11px] text-gray-800 whitespace-nowrap">{priceText}</span>
            </div>
          )}
        </div>
        {renderStatusRow(item)}
        <div className="mt-2.5 flex items-center justify-end gap-1.5">
          {item.stage === "appraisal" && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setEditingProperty(p);
              }}
              className="flex-1 px-2 py-1.5 rounded-lg border border-gray-200 text-gray-700 text-[11px] font-bold flex items-center justify-center gap-1 hover:bg-gray-50"
            >
              <Pencil className="w-3 h-3" /> Edit
            </button>
          )}
          {renderMenu(item)}
        </div>
      </div>
    );
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  const tile = (label, value, onClick, alert = false) => (
    <button
      key={label}
      type="button"
      onClick={onClick}
      className="bg-white border border-gray-200 rounded-xl px-4 py-3 text-left hover:shadow-sm transition-shadow"
    >
      <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400">{label}</p>
      <p className={`text-2xl font-bold mt-1 ${alert ? "text-rose-500" : "text-gray-900"}`}>{value}</p>
    </button>
  );

  const sortHeader = (id, children) => (
    <th key={id} className="text-left font-semibold px-4 py-3">
      <button type="button" onClick={() => toggleSort(id)} className="inline-flex items-center gap-1 uppercase hover:text-gray-800">
        {children}
        {sort.key === id && (sort.dir === "asc" ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />)}
      </button>
    </th>
  );

  // A tile opens the list on its stage, so the number turns into the rows.
  const openList = (stageId, nextSort) => {
    const next = new URLSearchParams(searchParams);
    next.set("view", "list");
    next.set("stage", stageId);
    setSearchParams(next, { replace: true });
    if (nextSort) setSort(nextSort);
  };

  const newCount = countFor("new");

  return (
    <div className="p-3 sm:p-4 md:p-6 bg-[#FDFDFD] min-h-screen">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-3 mb-5">
        <div className="min-w-0">
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#2b9aa4]">Prospects workspace</p>
          <h1 className="text-2xl md:text-[28px] font-bold text-gray-900 tracking-tight">Prospects</h1>
          <p className="text-sm text-gray-500">
            One workspace for every seller lead, from raw contact through to sale. Toggle between your working pipeline and your contact Database.
          </p>
        </div>
        {mode === "pipeline" && (
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={openImport}
              className="px-4 py-2.5 rounded-lg border border-gray-200 bg-white text-sm font-bold text-gray-700 flex items-center gap-2 hover:bg-gray-50"
            >
              <Upload className="w-4 h-4" /> Import
            </button>
            {canExport && (
              <button
                type="button"
                onClick={handleExport}
                className="px-4 py-2.5 rounded-lg border border-gray-200 bg-white text-sm font-bold text-gray-700 flex items-center gap-2 hover:bg-gray-50"
              >
                <Download className="w-4 h-4" /> Export
              </button>
            )}
            <button
              type="button"
              onClick={() => setAddOpen(true)}
              className="px-4 py-2.5 rounded-lg text-white text-sm font-bold flex items-center gap-2 shadow-md"
              style={{ background: TEAL }}
            >
              <Plus className="w-4 h-4" /> Add Lead
            </button>
          </div>
        )}
      </div>

      {/* Pipeline / Database toggle, market chips, scope */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 mb-5">
        <div className="inline-flex bg-gray-100 rounded-xl p-1 self-start" role="tablist" aria-label="Prospects mode">
          <button
            type="button"
            role="tab"
            aria-selected={mode === "pipeline"}
            onClick={() => setParam("mode", "pipeline", "pipeline")}
            className={`px-4 py-2 rounded-lg text-sm font-bold flex items-center gap-2 transition-colors ${
              mode === "pipeline" ? "bg-white text-[#2b9aa4] shadow-sm" : "text-gray-500 hover:text-gray-700"
            }`}
          >
            Pipeline
            {newCount > 0 && (
              <span className="px-1.5 py-0.5 rounded-full bg-rose-500 text-white text-[10px] font-bold">
                {newCount > 99 ? "99+" : newCount} new
              </span>
            )}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === "database"}
            onClick={() => setParam("mode", "database", "pipeline")}
            className={`px-4 py-2 rounded-lg text-sm font-bold transition-colors ${
              mode === "database" ? "bg-white text-[#2b9aa4] shadow-sm" : "text-gray-500 hover:text-gray-700"
            }`}
          >
            Database
          </button>
        </div>
        {mode === "pipeline" && (
          <div className="flex items-center gap-2 flex-wrap">
            {MARKETS.map((m) => (
              <button
                key={m.id}
                type="button"
                onClick={() => setParam("market", m.id, "all")}
                className={`px-3.5 py-1.5 rounded-full text-xs font-bold border transition-colors ${
                  market === m.id
                    ? "bg-gray-900 border-gray-900 text-white"
                    : "bg-white border-gray-200 text-gray-600 hover:border-gray-300"
                }`}
              >
                {m.label}
              </button>
            ))}
            <button
              type="button"
              aria-pressed={buyerMatch}
              onClick={() => {
                setBuyerMatch((on) => !on);
                setBuyerCounts(null);
              }}
              title="Show how many CRM buyers match each property"
              className={`px-3.5 py-1.5 rounded-full text-xs font-bold border transition-colors inline-flex items-center gap-1.5 ${
                buyerMatch
                  ? "bg-[#6D28D9] border-[#6D28D9] text-white"
                  : "bg-white border-gray-200 text-gray-600 hover:border-gray-300"
              }`}
            >
              <Users className="w-3.5 h-3.5" /> Buyer Match
              {buyerMatch && buyerCounts && (
                <span className="font-semibold opacity-90">
                  · {matchedCards} with buyers
                </span>
              )}
            </button>
            {pickerMode ? (
              <AgentScopePicker scope={scope} />
            ) : (
              canSwitch && (
                <AgencyScopeSwitch
                  agencyWide={agencyWide}
                  onChange={setAgencyWide}
                  mineLabel="My pipeline"
                  agencyLabel="All agents"
                />
              )
            )}
          </div>
        )}
      </div>

      {mode === "database" ? (
        <DatabasePage embedded />
      ) : (
        <>
          {/* Tiles */}
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 mb-5">
            {tile("Total leads", totalCount, () => setParam("stage", null))}
            {tile("New", newCount, () => openList("new"))}
            {tile("At risk", atRiskCount, () => openList("new", { key: "days", dir: "desc" }), atRiskCount > 0)}
            {tile("Listed", countFor("listed"), () => openList("listed"))}
            {tile("For Sale", countFor("for_sale"), () => openList("for_sale"))}
            {tile("Lost", countFor("lost"), () => openList("lost"))}
          </div>

          {/* Search + Kanban / List */}
          <div className="flex flex-col sm:flex-row gap-2 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search leads / property..."
                className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#55C5D0]"
              />
            </div>
            <div className="inline-flex bg-gray-100 rounded-lg p-1 self-start" role="tablist" aria-label="Pipeline view">
              <button
                type="button"
                role="tab"
                aria-selected={view === "kanban"}
                onClick={() => setParam("view", "kanban", "kanban")}
                className={`px-3 py-1.5 rounded-md text-xs font-bold flex items-center gap-1.5 ${
                  view === "kanban" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500"
                }`}
              >
                <LayoutGrid className="w-3.5 h-3.5" /> Kanban
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={view === "list"}
                onClick={() => setParam("view", "list", "kanban")}
                className={`px-3 py-1.5 rounded-md text-xs font-bold flex items-center gap-1.5 ${
                  view === "list" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500"
                }`}
              >
                <ListIcon className="w-3.5 h-3.5" /> List
              </button>
            </div>
            <button
              type="button"
              onClick={load}
              className="px-3 py-2 border border-gray-200 bg-white rounded-lg text-xs font-bold text-gray-600 hover:bg-gray-50 flex items-center gap-1.5 self-start"
            >
              <RefreshCw className="w-3.5 h-3.5" /> Refresh
            </button>
          </div>

          {/* Stage legend. On the list it doubles as the stage filter. */}
          <div className="flex items-center gap-2 flex-wrap mb-4 text-xs">
            {view === "list" && (
              <button
                type="button"
                onClick={() => setParam("stage", null)}
                className={`px-2.5 py-1 rounded-full font-bold border ${
                  stageFilter === "all" ? "bg-gray-900 border-gray-900 text-white" : "bg-white border-gray-200 text-gray-600"
                }`}
              >
                All stages
              </button>
            )}
            {PROSPECT_STAGES.map((s) =>
              view === "list" ? (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => setParam("stage", s.id, "all")}
                  className={`px-2.5 py-1 rounded-full font-bold border flex items-center gap-1.5 ${
                    stageFilter === s.id ? "bg-gray-900 border-gray-900 text-white" : "bg-white border-gray-200 text-gray-600"
                  }`}
                >
                  <span className="w-2 h-2 rounded-sm" style={{ background: s.color }} />
                  {s.label} ({countFor(s.id)})
                </button>
              ) : (
                <span key={s.id} className="flex items-center gap-1.5 text-gray-600 font-medium">
                  <span className="w-2.5 h-2.5 rounded-sm" style={{ background: s.color }} />
                  {s.label}
                  {s.id === "new" ? ", not yet contacted" : ""}
                </span>
              )
            )}
          </div>

          {loading ? (
            <div className="py-16 text-center text-gray-500 text-sm">Loading the pipeline...</div>
          ) : view === "kanban" ? (
            <DragDropContext onDragEnd={handleDragEnd}>
            <div className="overflow-x-auto pb-3 custom-scrollbar">
              <div className="grid grid-cols-8 gap-3 min-w-[1880px]">
                {PROSPECT_STAGES.map((s) => {
                  const items = byStage[s.id];
                  const foot = columnFoot(s.id);
                  const shown = countFor(s.id);
                  return (
                    <div
                      key={s.id}
                      role="region"
                      aria-label={`${s.label} column`}
                      className="bg-[#F4F6F7] rounded-xl border-t-4 flex flex-col"
                      style={{ borderTopColor: s.color }}
                    >
                      <div className="px-3 pt-3 pb-2">
                        <div className="flex items-center justify-between">
                          <h2 className="text-xs font-bold uppercase tracking-wider text-gray-800">{s.label}</h2>
                          <span className="px-2 py-0.5 rounded-full bg-white text-[11px] font-bold text-gray-600">{shown}</span>
                        </div>
                        <p className={`text-[11px] mt-0.5 ${foot.alert ? "text-rose-500 font-semibold" : "text-gray-500"}`}>
                          {foot.text}
                        </p>
                        {(s.id === "appraisal" || s.id === "connected" || s.id === "long_term") && (
                          <div className="mt-1.5 inline-flex bg-white rounded-lg p-0.5 text-[10px] font-bold" role="group" aria-label={`Sort ${s.label} by temperature`}>
                            {[
                              { id: null, label: "All" },
                              { id: "hot", label: "Hot first" },
                              { id: "cold", label: "Cold first" },
                            ].map((o) => (
                              <button
                                key={o.label}
                                type="button"
                                aria-pressed={kanbanTempSort[s.id] === o.id}
                                onClick={() => setKanbanTempSort((prev) => ({ ...prev, [s.id]: o.id }))}
                                className={`px-2 py-0.5 rounded-md ${kanbanTempSort[s.id] === o.id ? "text-white" : "text-gray-500 hover:text-gray-800"}`}
                                style={kanbanTempSort[s.id] === o.id ? { background: s.color } : undefined}
                              >
                                {o.label}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      <Droppable droppableId={s.id}>
                        {(drop, dropSnap) => (
                          <div
                            ref={drop.innerRef}
                            {...drop.droppableProps}
                            className={`flex-1 px-2 pb-2 max-h-[72vh] min-h-[120px] overflow-y-auto custom-scrollbar rounded-b-xl transition-colors ${
                              dropSnap.isDraggingOver ? "bg-[#e6f7f9]" : ""
                            }`}
                          >
                            {items.length === 0 && !dropSnap.isDraggingOver && (
                              <p className="text-center text-xs text-gray-400 py-8">Nothing here yet</p>
                            )}
                            {items.map((item, index) => (
                              <Draggable
                                key={item.key}
                                draggableId={item.key}
                                index={index}
                                isDragDisabled={busyKey === item.key || isSold(item)}
                              >
                                {(drag, dragSnap) => (
                                  <div
                                    ref={drag.innerRef}
                                    {...drag.draggableProps}
                                    {...drag.dragHandleProps}
                                    className={`mb-2 ${dragSnap.isDragging ? "rotate-1 opacity-90" : ""}`}
                                    style={drag.draggableProps.style}
                                  >
                                    {item.stage === "lost"
                                      ? renderLostCard(item)
                                      : item.kind === "lead"
                                      ? renderLeadCard(item)
                                      : renderPropertyCard(item)}
                                  </div>
                                )}
                              </Draggable>
                            ))}
                            {drop.placeholder}
                            {unfiltered && shown > items.length && (
                              <p className="text-center text-[11px] text-gray-400 py-1">
                                Showing {items.length} of {shown}. Search to find the rest.
                              </p>
                            )}
                          </div>
                        )}
                      </Droppable>
                    </div>
                  );
                })}
              </div>
            </div>
            </DragDropContext>
          ) : listRows.length === 0 ? (
            <div className="py-16 text-center text-gray-500 text-sm border border-dashed border-gray-200 rounded-xl">
              Nothing matches.
            </div>
          ) : (
            <div className="border border-gray-200 rounded-xl overflow-x-auto bg-white">
              <table className="w-full min-w-[1100px] text-sm">
                <thead className="bg-gray-50 text-gray-500 text-[11px]">
                  <tr>
                    {sortHeader("title", "Lead / Property")}
                    {/* Its own column while Buyer Match is on. It used to be a
                        chip at the end of Source, far right, and read as the
                        toggle doing nothing (Christian Gravias, DOMA
                        13374913603, 26 Oct 2026: "I cant see the buyer match"). */}
                    {buyerMatch && <th className="text-left font-semibold px-4 py-3 uppercase">Buyers</th>}
                    {sortHeader("stage", "Stage")}
                    {sortHeader("temperature", "Temperature")}
                    {sortHeader("market", "Market")}
                    {sortHeader("timeframe", "Selling Time Frame")}
                    {sortHeader("days", "Days in stage")}
                    {sortHeader("activity", "Last activity")}
                    {sortHeader("assigned", "Assigned")}
                    {sortHeader("source", "Source")}
                    <th className="px-2 py-3" aria-label="Actions" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {listRows.map((item) => {
                    const s = STAGE_BY_ID[item.stage];
                    const days = daysSince(item.enteredAt);
                    const risk = isAtRisk(item);
                    return (
                      <tr key={item.key} onClick={() => openItem(item)} className="hover:bg-gray-50 cursor-pointer align-top">
                        <td className="px-4 py-3">
                          <p className="font-bold text-gray-900">{item.person || item.title}</p>
                          <p className="text-xs text-gray-500">{item.fullAddress || item.subtitle}</p>
                          {item.databaseRecord && <PublicPropertyLink record={item.databaseRecord} className="mt-1 mr-1" />}
                          {item.propertyProfile && <PropertyProfileLink profile={item.propertyProfile} className="mt-1 mr-1" />}
                          {item.kind === "lead" && (
                            <UpdatePropertyInfoButton leadId={item.raw?.id} address={item.fullAddress} className="mt-1" />
                          )}
                          {(item.phone || item.email) && (
                            <p className="text-xs text-gray-500 mt-0.5 flex flex-wrap gap-x-3">
                              {item.phone && (
                                <a href={`tel:${item.phone.replace(/\s+/g, "")}`} onClick={(e) => e.stopPropagation()} className="hover:text-[#2b9aa4]">
                                  {item.phone}
                                </a>
                              )}
                              {item.email && (
                                <a href={`mailto:${item.email}`} onClick={(e) => e.stopPropagation()} className="hover:text-[#2b9aa4] break-all">
                                  {item.email}
                                </a>
                              )}
                            </p>
                          )}
                        </td>
                        {buyerMatch && (
                          <td className="px-4 py-3">
                            {renderBuyerChip(item) || <span className="text-gray-300">-</span>}
                          </td>
                        )}
                        <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                          {/* Change the stage straight from the row: the same
                              moves, and the same actions, as dragging the card. */}
                          <select
                            aria-label={`Stage for ${item.title}`}
                            value={item.stage}
                            disabled={busyKey === item.key || isSold(item)}
                            title={isSold(item) ? "A sold property stays in Sold" : undefined}
                            onChange={(e) => moveItem(item, e.target.value)}
                            className="px-2 py-1 rounded-md text-[11px] font-bold border-0 cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#55C5D0]"
                            style={{ background: `${s.color}22`, color: s.color }}
                          >
                            {/* Each choice in its own stage's colour, so
                                Listed reads aqua and Lost red (Christian
                                Gravias, Monday 12988442847, 14 Oct 2026:
                                "Make the colours match the stage"). */}
                            <option value={item.stage} style={{ color: s.color, background: "#fff" }}>
                              {s.label}
                            </option>
                            {allowedMoves(item).map((id) => (
                              <option key={id} value={id} style={{ color: STAGE_BY_ID[id].color, background: "#fff", fontWeight: 700 }}>
                                Move to {STAGE_BY_ID[id].label}
                              </option>
                            ))}
                          </select>
                          {/* Called straight from the row (Christian Gravias,
                              Monday 12988442847, 16 Oct 2026: "Add a called
                              button under the new button for list view"). */}
                          {item.kind === "lead" && item.stage === "new" && (
                            <button
                              type="button"
                              disabled={busyKey === item.key}
                              onClick={() => handleCalled(item)}
                              title="I have spoken to them. Moves the lead to Connected."
                              className="mt-1.5 px-2 py-1 rounded-md border border-gray-200 text-gray-700 text-[11px] font-bold flex items-center gap-1 hover:bg-gray-50 disabled:opacity-60"
                            >
                              <Phone className="w-3 h-3" /> Called
                            </button>
                          )}
                          {/* Assign to agent, under Called, for a super admin
                              (Christian Gravias, Monday 12988442847, 17 Oct
                              2026). Shows who has it now; clicking opens the
                              hand-over panel with the agent picker and note. */}
                          {canAssignLeads && item.kind === "lead" && item.stage === "new" && (
                            <button
                              type="button"
                              disabled={busyKey === item.key}
                              onClick={() => openAssignLead(item)}
                              aria-label={`Assign ${item.title} to an agent`}
                              title="Assign to agent"
                              className="mt-1.5 max-w-[180px] px-2 py-1 rounded-md border border-dashed border-[#55C5D0] text-[#2b9aa4] text-[11px] font-bold flex items-center gap-1 hover:bg-[#eefafb] disabled:opacity-60"
                            >
                              <UserPlus className="w-3 h-3 shrink-0" />
                              <span className="truncate">{item.assigned || "Assign to agent"}</span>
                              <ChevronDown className="w-3 h-3 shrink-0" />
                            </button>
                          )}
                        </td>
                        <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                          {hasTemperature(item) ? renderTemperature(item) : <span className="text-gray-300">-</span>}
                        </td>
                        <td className="px-4 py-3 text-gray-700" onClick={(e) => canChangeMarket(item) && e.stopPropagation()}>
                          {renderMarket(item, { list: true })}
                        </td>
                        <td className="px-4 py-3 text-gray-700">{item.timeframe || "-"}</td>
                        <td className={`px-4 py-3 ${risk ? "text-rose-500 font-bold" : "text-gray-700"}`}>
                          {risk ? "> 24h old" : days === null ? "-" : `${days} day${days === 1 ? "" : "s"}`}
                        </td>
                        <td
                          className="px-4 py-3 text-gray-600 max-w-[280px] cursor-pointer hover:text-[#128f89]"
                          title="Open Agent Notes"
                          onClick={(e) => { e.stopPropagation(); openNotes(item); }}
                        >
                          <span className="line-clamp-2">{item.lostReason || item.lastActivity || "-"}</span>
                        </td>
                        {/* Assigned is a button on every tab, so the agent can be
                            changed from the row (Christian Gravias, 21 Oct 2026,
                            CRM-2026-00113). Leads open the hand-over panel,
                            appraisals and listings the Change Agent panel. */}
                        <td className="px-4 py-3 text-gray-700" onClick={(e) => canAssignLeads && e.stopPropagation()}>
                          {canAssignLeads ? (
                            <button
                              type="button"
                              disabled={busyKey === item.key}
                              onClick={() => openAssignLead(item)}
                              aria-label={`Change agent for ${item.title}`}
                              title="Change agent"
                              className="max-w-[180px] px-2 py-1 rounded-md border border-gray-200 text-gray-700 text-xs font-semibold flex items-center gap-1 hover:border-[#55C5D0] hover:text-[#2b9aa4] hover:bg-[#eefafb] disabled:opacity-60"
                            >
                              <span className="truncate">{item.assigned || "Unassigned"}</span>
                              <ChevronDown className="w-3 h-3 shrink-0" />
                            </button>
                          ) : (
                            item.assigned || "Unassigned"
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <LeadSourceBadge source={item.source} />
                          <RentalLeadPill transactionType={item.transactionType} className="ml-1" />
                        </td>
                        <td className="px-2 py-3" onClick={(e) => e.stopPropagation()}>
                          {menuEntries(item).length > 0 && renderMenu(item)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* The old pages' extra views, still one click away. */}
          <div className="mt-5 text-xs text-gray-500 flex flex-wrap items-center gap-x-4 gap-y-1">
            <span className="font-semibold text-gray-400">Also in Prospects:</span>
            <button type="button" onClick={() => navigate("/seller-leads?market=actioned")} className="hover:text-[#2b9aa4] hover:underline">
              Actioned and ignored leads
            </button>
            <button type="button" onClick={() => navigate("/off-market-pipeline?priority=lost")} className="hover:text-[#2b9aa4] hover:underline">
              Lost appraisals
            </button>
            <button type="button" onClick={() => navigate("/off-market-pipeline")} className="hover:text-[#2b9aa4] hover:underline">
              Off Market appraisal board
            </button>
            <button type="button" onClick={() => navigate("/on-market-pipeline")} className="hover:text-[#2b9aa4] hover:underline">
              On Market appraisal board
            </button>
          </div>
        </>
      )}

      {buyersFor && <ProspectBuyersModal item={buyersFor} onClose={() => setBuyersFor(null)} />}
      <AddSellerLeadModal
        isOpen={addOpen}
        onClose={() => setAddOpen(false)}
        defaultMarket={market === "on_market" ? "on_market" : "off_market"}
        onSaved={load}
      />
      {importLists && (
        <ImportWizard
          view="owners"
          lists={importLists}
          pipeline
          onClose={() => setImportLists(null)}
          onDone={load}
        />
      )}
      <LeadNotesModal isOpen={!!notesLead} lead={notesLead} onClose={() => setNotesLead(null)} onSaved={handleNoteSaved} />
      <PropertyNotesModal isOpen={!!notesProperty} property={notesProperty} onClose={() => setNotesProperty(null)} />
      <AssignLeadModal
        isOpen={!!assignLead}
        lead={assignLead}
        agents={agents}
        onClose={() => setAssignLead(null)}
        onAssigned={handleLeadAssigned}
      />
      <AssignPropertyAgentModal
        isOpen={!!assignProperty}
        property={assignProperty?.raw}
        label={assignProperty?.fullAddress || assignProperty?.title || ""}
        agents={agents}
        onClose={() => setAssignProperty(null)}
        onAssigned={load}
      />
      <LeadAppraisalEditModal
        isOpen={!!editLead}
        lead={editLead?.raw}
        stage={editLead?.stage}
        onClose={() => setEditLead(null)}
        onSaved={handleLeadEdited}
        onAddAsAppraisal={() => editLead && handleAddAsAppraisal(editLead)}
        onAddToDatabase={() => editLead && setDbLead(editLead.raw)}
        onListed={() => editLead && handleLeadListed(editLead)}
        onLost={(reason, note) => editLead && handleLeadLost(editLead, reason, note)}
      />
      <AddToDatabaseModal
        isOpen={!!dbLead}
        lead={dbLead}
        onClose={() => {
          setDbLead(null);
          setDbFromLost(false);
        }}
        onSaved={handleLeadFiled}
      />
      <AddToDatabaseModal
        isOpen={!!dbProperty}
        property={dbProperty}
        onClose={() => setDbProperty(null)}
        onSaved={() => setDbProperty(null)}
      />
      <IgnoreLeadReasonModal
        open={!!ignoreTarget}
        busy={!!ignoreTarget && busyKey === ignoreTarget.key}
        onClose={() => setIgnoreTarget(null)}
        onConfirm={(reason, notes) => {
          const item = ignoreTarget;
          setIgnoreTarget(null);
          if (item) handleIgnore(item, reason, notes);
        }}
      />
      {lostTarget && (
        <div
          className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-label="Why was it lost?"
        >
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-5">
            <div className="flex items-start justify-between gap-3 mb-3">
              <div className="min-w-0">
                <h3 className="text-base font-bold text-gray-900">Lost, why?</h3>
                <p className="text-xs text-gray-500 break-words">{lostTarget.person || lostTarget.title}, {lostTarget.fullAddress || lostTarget.subtitle}</p>
              </div>
              <button type="button" aria-label="Cancel" onClick={() => setLostTarget(null)} className="p-1 rounded-md text-gray-400 hover:bg-gray-100">
                <X className="w-4 h-4" />
              </button>
            </div>
            {isListing(lostTarget) && (
              <p className="mb-3 text-xs text-gray-500">
                This withdraws the listing, and takes it off REA if the CRM published it there.
              </p>
            )}
            <div className="space-y-1.5">
              <LostReasonPicker
                reasons={isListing(lostTarget) ? LISTING_LOST_REASONS : LOST_REASONS}
                onPick={(reason, note) => confirmLost(lostTarget, reason, note)}
              />
            </div>
          </div>
        </div>
      )}
      <MarkSoldModal
        isOpen={!!soldTarget}
        onClose={() => setSoldTarget(null)}
        onConfirm={confirmSold}
        loading={markingSold}
        address={soldTarget?.raw?.address}
        initialSoldPrice={soldTarget?.raw?.sold_price || soldTarget?.raw?.price}
        initialSuppress={soldTarget?.raw?.suppress_sold_price}
        agents={agents}
        primaryAgentId={soldTarget?.raw?.agent?.id ?? ""}
        primaryAgentName={soldTarget?.raw?.agent?.name ?? ""}
        listingCoAgent={soldTarget?.raw?.co_agent ?? null}
      />
      <PipelineAppraisalEditModal
        isOpen={!!editingProperty}
        property={editingProperty}
        saving={savingEdit}
        onClose={() => setEditingProperty(null)}
        onSave={handleSaveAppraisal}
        onAddToDatabase={(property) => setDbProperty(property)}
        onListed={handleAppraisalListed}
        onLost={handleAppraisalLost}
      />
    </div>
  );
};

export default ProspectsPage;
